/**
 * Capital actions (C26): the Settings card "رأس المال المستثمر" (App.tsx:15415-15536) and the dashboard's capital
 * history (App.tsx:50580-50610) open the same dialogs. Add / withdraw post `capital_add` / `capital_withdraw` on the main
 * IQD vault through the gateway (preview → PIN → commit, D-018, D-232; the engine routes to the recorder's sub-safe when
 * sub-safes are on); the D-076 full-balance withdrawal posts `capital_withdraw` with `fullBalance` on the chosen vault or
 * box (the amount is the server's: the current ledger balance). The capitalization reuses the Profits screen's dialog
 * (C25) with the pending figures of the dashboard. Permissions: `manage_capital` (D-263 (a)); `settle_profit` for the
 * capitalization (D-255 (c)). Nothing is computed here (ADR-02 §2.9).
 */
import { useState, type ReactNode } from "react";
import { AddCapitalModal, CapitalizeProfitModal, FullBalanceWithdrawModal, WithdrawCapitalModal, parseCommas, toEnglishDigits, type CapitalValues, type FullBalanceBox, type ProfitSettlementValues } from "@faylasuf/ui";
import { useApp, useSession } from "../../app/context.js";
import { useGateway } from "../../app/operations.js";
import { useToast } from "../../app/toast.js";
import { fmt, isZero } from "../customers/format.js";
import { useAccounts } from "../WalletsPage.js";

const amountOf = (v: string): string => toEnglishDigits(parseCommas(v)).trim();
const EMPTY: CapitalValues = { amount: "", note: "" };

export interface CapitalActions {
  readonly canManage: boolean;
  readonly canCapitalize: boolean;
  readonly openAdd: () => void;
  readonly openWithdraw: () => void;
  readonly openFullBalance: () => void;
  readonly openCapitalize: () => void;
  readonly dialogs: ReactNode;
}

/** `pending`: pending profit labels for the capitalization dialog (null = not available to this viewer). */
export function useCapitalActions(pending: { readonly IQD: string; readonly USD: string | null } | null, subSafesActive = false): CapitalActions {
  const { t } = useApp();
  const { session, hasPermission } = useSession();
  const toast = useToast();
  const gateway = useGateway();
  const canManage = hasPermission("manage_capital");
  const canCapitalize = hasPermission("settle_profit") && pending !== null;
  const accounts = useAccounts(canManage);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addValues, setAddValues] = useState<CapitalValues>(EMPTY);
  const [wdOpen, setWdOpen] = useState(false);
  const [wdValues, setWdValues] = useState<CapitalValues>(EMPTY);
  const [fullOpen, setFullOpen] = useState(false);
  const [full, setFull] = useState<{ boxId: string; note: string }>({ boxId: "", note: "" });
  const [capOpen, setCapOpen] = useState(false);
  const [capValues, setCapValues] = useState<ProfitSettlementValues>({ currency: "IQD", amount: "", note: "" });

  const rows = accounts.data ?? [];
  const branchId = session.session.branchIds[0];
  const mainVault = rows.find((a) => a.kind === "cash_vault" && a.currency === "IQD" && !a.archived && (!branchId || a.branchId === branchId))?.id ?? null;
  // D-076: a vault or a box (IQD) of the session's branch. With sub-safes on the engine takes a vault's withdrawal from the
  // recorder's own custody (D-118, `cashAccountFor`), so that is the balance shown and confirmed (review C26 #3).
  const boxes: FullBalanceBox[] = rows
    .filter((a) => !a.archived && a.currency === "IQD" && (!branchId || a.branchId === branchId) && (a.kind === "cash_vault" || a.kind === "cash_box" || a.kind === "digital_box"))
    .map((a) => {
      let available = a.available; // engine: balance − pending reservations
      if (a.kind === "cash_vault" && subSafesActive) {
        const custodies = rows.filter((x) => x.kind === "custody" && x.parentAccountId === a.id && !x.archived);
        if (custodies.length > 0) available = custodies.find((x) => x.ownerId === session.user.id)?.available ?? "0";
      }
      return { id: a.id, name: a.name ?? "", balanceLabel: fmt(available), withdrawable: !isZero(available) && !available.startsWith("-") };
    });

  const post = async (type: string, input: Record<string, unknown>, pinMessage: string, successKey: string, close: () => void) => {
    setBusy(true);
    try {
      const res = await gateway.run(type, input, { pinMessage, successKey, allowSupervisor: true });
      if (res) close();
    } catch {
      /* toasted by the gateway */
    } finally {
      setBusy(false);
    }
  };
  const submitCapital = (kind: "add" | "withdraw") => {
    const v = kind === "add" ? addValues : wdValues;
    const amount = amountOf(v.amount);
    if (!amount || isZero(amount)) return toast(t("cap.err_amount"), "error");
    if (!mainVault) return toast(t("cap.err_box"), "error");
    const input: Record<string, unknown> = { amount, accountId: mainVault, ...(v.note.trim() ? { note: v.note.trim() } : {}) };
    const label = `${fmt(amount)} ${t("unit.iqd")}`;
    void post(
      kind === "add" ? "capital_add" : "capital_withdraw",
      input,
      t(kind === "add" ? "cap.pin_add" : "cap.pin_withdraw", { amount: label }),
      kind === "add" ? "cap.toast_added" : "cap.toast_withdrawn",
      () => {
        if (kind === "add") {
          setAddOpen(false);
          setAddValues(EMPTY);
        } else {
          setWdOpen(false);
          setWdValues(EMPTY);
        }
      },
    );
  };
  const submitFull = () => {
    const box = boxes.find((b) => b.id === full.boxId);
    if (!box) return toast(t("cap.err_box"), "error");
    void post(
      "capital_withdraw",
      { fullBalance: true, accountId: box.id, ...(full.note.trim() ? { note: full.note.trim() } : {}) },
      t("cap.pin_full", { name: box.name, amount: `${box.balanceLabel} ${t("unit.iqd")}` }),
      "cap.toast_withdrawn",
      () => {
        setFullOpen(false);
        setFull({ boxId: "", note: "" });
      },
    );
  };
  const submitCapitalize = () => {
    const amount = amountOf(capValues.amount);
    if (!amount || isZero(amount)) return toast(t("pf.err_amount"), "error");
    const label = capValues.currency === "USD" ? `$${fmt(amount)}` : `${fmt(amount)} د.ع`;
    void post("profit_capitalize", { currency: capValues.currency, amount, ...(capValues.note.trim() ? { note: capValues.note.trim() } : {}) }, t("pf.pin_capitalize", { amount: label }), "pf.toast_capitalized", () => {
      setCapOpen(false);
      setCapValues({ currency: "IQD", amount: "", note: "" });
    });
  };

  return {
    canManage,
    canCapitalize,
    openAdd: () => setAddOpen(true),
    openWithdraw: () => setWdOpen(true),
    openFullBalance: () => setFullOpen(true),
    openCapitalize: () => setCapOpen(true),
    dialogs: (
      <>
        <AddCapitalModal open={addOpen} values={addValues} onChange={setAddValues} busy={busy} onSubmit={() => submitCapital("add")} onClose={() => setAddOpen(false)} />
        <WithdrawCapitalModal open={wdOpen} values={wdValues} onChange={setWdValues} busy={busy} onSubmit={() => submitCapital("withdraw")} onClose={() => setWdOpen(false)} />
        <FullBalanceWithdrawModal open={fullOpen} boxes={boxes} boxId={full.boxId} note={full.note} onChange={setFull} busy={busy} onSubmit={submitFull} onClose={() => setFullOpen(false)} />
        {pending && <CapitalizeProfitModal open={capOpen} values={capValues} onChange={setCapValues} pending={pending} busy={busy} onSubmit={submitCapitalize} onClose={() => setCapOpen(false)} />}
        {gateway.pin.dialog}
      </>
    ),
  };
}
